import React, { createContext, useContext, useCallback, useRef, useState, useEffect } from "react";
import { useUserData } from "../hooks/useUserData";
import { getAnalytics, saveAnalytics, MAX_VISIT_HISTORY } from "../data/analyticsStorage";
import {
  USE_TIMELINE_BACKEND,
  getTimelineAnalyticsFromBackend,
  saveTimelineAnalyticsToBackend,
} from "../services/analyticsApi";
import { getPathLabel } from "../data/pathLabels";
import { getAndResetCounters, setPageLoadTime } from "../services/visitTrackingCounters";

const VisitTrackingContext = createContext(null);

export function useVisitTracking() {
  const ctx = useContext(VisitTrackingContext);
  return ctx || { recentVisits: [], pageStats: {}, visitHistory: [], trackPageEnter: () => {}, trackPageLeave: () => {}, trackClick: () => {}, recordPageLoadTime: () => {}, addRecentVisit: () => {}, updateRecentVisitSubtitle: () => {} };
}

const SAVE_TO_BACKEND_INTERVAL_MS = 30 * 1000; // 30 seconds; backend can process at this interval

export function VisitTrackingProvider({ children }) {
  const { userData } = useUserData();
  const userId = userData?.StaffId ?? "";
  const organizationId = userData?.OrganizationId ?? "";
  const [recentVisits, setRecentVisits] = useState([]);
  const [pageStats, setPageStats] = useState({});
  const [visitHistory, setVisitHistory] = useState([]);
  const enterTimeRef = useRef(null);
  const currentPathRef = useRef(null);
  const clickCountRef = useRef(0);
  const throttleClickRef = useRef(0);
  const lastRecentVisitsRef = useRef([]);
  const lastSaveRef = useRef({ recentVisits: [], pageStats: {}, visitHistory: [] });

  const load = useCallback(async () => {
    if (USE_TIMELINE_BACKEND && userId && organizationId) {
      const fromApi = await getTimelineAnalyticsFromBackend(userId, organizationId);
      if (fromApi) {
        setRecentVisits(fromApi.recentVisits || []);
        setPageStats(fromApi.pageStats || {});
        setVisitHistory(fromApi.visitHistory || []);
        return;
      }
    }
    const { recentVisits: r, pageStats: s, visitHistory: v } = getAnalytics(userId);
    setRecentVisits(r);
    setPageStats(s);
    setVisitHistory(v || []);
  }, [userId, organizationId]);

  useEffect(() => {
    load();
  }, [load, userId]);

  useEffect(() => {
    lastRecentVisitsRef.current = recentVisits;
  }, [recentVisits]);

  useEffect(() => {
    if (!userId) return;
    saveAnalytics(userId, { recentVisits, pageStats, visitHistory });
    lastSaveRef.current = { recentVisits, pageStats, visitHistory };
  }, [userId, recentVisits, pageStats, visitHistory]);

  useEffect(() => {
    if (!USE_TIMELINE_BACKEND || !userId || !organizationId) return;
    const id = setInterval(() => {
      saveTimelineAnalyticsToBackend(userId, organizationId, lastSaveRef.current);
    }, SAVE_TO_BACKEND_INTERVAL_MS);
    return () => clearInterval(id);
  }, [userId, organizationId]);

  useEffect(() => {
    if (!USE_TIMELINE_BACKEND || !userId || !organizationId) return;
    const onBeforeUnload = () => {
      saveTimelineAnalyticsToBackend(userId, organizationId, lastSaveRef.current);
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [userId, organizationId]);

  const addRecentVisit = useCallback((path, label, subtitle) => {
    if (!path || path === "/login" || path === "/forgot-password") return;
    const visitedAt = Date.now();
    setRecentVisits((prev) => {
      let next = prev.filter((v) => v.path !== path);
      if (path.startsWith("/isf") && path !== "/isf" && path.includes("?")) {
        next = next.filter((v) => v.path !== "/isf");
      }
      return [{ path, label, visitedAt, subtitle: subtitle || undefined }, ...next].slice(0, 10);
    });
  }, []);

  const updateRecentVisitSubtitle = useCallback((path, subtitle) => {
    if (!path || !subtitle) return;
    setRecentVisits((prev) => {
      const idx = prev.findIndex((v) => v.path === path);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], subtitle };
      return next;
    });
  }, []);

  const pushVisitToHistory = useCallback((path, visitedAt, timeSpentMs, clickCount) => {
    const label = getPathLabel(path);
    const recent = lastRecentVisitsRef.current;
    const rec = recent.find((r) => r.path === path);
    const subtitle = rec?.subtitle;
    const counters = getAndResetCounters();
    const entry = {
      path,
      label,
      visitedAt,
      subtitle: subtitle || undefined,
      timeSpentMs,
      clickCount: clickCount || 0,
      loadTimeMs: counters.loadTimeMs || undefined,
      apiErrorCount: counters.apiErrorCount || undefined,
      networkErrorCount: counters.networkErrorCount || undefined,
    };
    setVisitHistory((prev) => [entry, ...prev].slice(0, MAX_VISIT_HISTORY));
  }, []);

  const trackPageEnter = useCallback(
    (path) => {
      const prevPath = currentPathRef.current;
      const now = Date.now();
      if (prevPath && enterTimeRef.current != null) {
        const timeSpent = now - enterTimeRef.current;
        const clicks = clickCountRef.current;
        pushVisitToHistory(prevPath, enterTimeRef.current, timeSpent, clicks);
        setPageStats((prev) => {
          const stat = prev[prevPath] || { visitCount: 0, totalTimeSpentMs: 0, clickCount: 0 };
          return {
            ...prev,
            [prevPath]: {
              visitCount: stat.visitCount + 1,
              totalTimeSpentMs: stat.totalTimeSpentMs + timeSpent,
              clickCount: stat.clickCount + clicks,
            },
          };
        });
        clickCountRef.current = 0;
      }
      currentPathRef.current = path;
      enterTimeRef.current = now;
      const label = getPathLabel(path);
      addRecentVisit(path, label);
    },
    [addRecentVisit, pushVisitToHistory]
  );

  const trackPageLeave = useCallback(() => {
    const path = currentPathRef.current;
    if (!path || enterTimeRef.current == null) return;
    const timeSpent = Date.now() - enterTimeRef.current;
    const clicks = clickCountRef.current;
    pushVisitToHistory(path, enterTimeRef.current, timeSpent, clicks);
    setPageStats((prev) => {
      const stat = prev[path] || { visitCount: 0, totalTimeSpentMs: 0, clickCount: 0 };
      return {
        ...prev,
        [path]: {
          visitCount: stat.visitCount + 1,
          totalTimeSpentMs: stat.totalTimeSpentMs + timeSpent,
          clickCount: stat.clickCount + clicks,
        },
      };
    });
    clickCountRef.current = 0;
    currentPathRef.current = null;
    enterTimeRef.current = null;
  }, [pushVisitToHistory]);

  const trackClick = useCallback(() => {
    const now = Date.now();
    if (now - throttleClickRef.current < 500) return;
    throttleClickRef.current = now;
    clickCountRef.current = (clickCountRef.current || 0) + 1;
  }, []);

  const recordPageLoadTime = useCallback(() => {
    if (enterTimeRef.current != null) {
      setPageLoadTime(Date.now() - enterTimeRef.current);
    }
  }, []);

  const value = {
    recentVisits,
    pageStats,
    visitHistory,
    trackPageEnter,
    trackPageLeave,
    trackClick,
    recordPageLoadTime,
    addRecentVisit,
    updateRecentVisitSubtitle,
  };

  return (
    <VisitTrackingContext.Provider value={value}>
      {children}
    </VisitTrackingContext.Provider>
  );
}
